import deploymentsFile from "@content/qr/deployments.json";
import type {
  QrDeploymentRecord,
  QrResolveResult,
  QrSessionContext,
} from "./types";

type DeploymentsFile = {
  schema: string;
  version: number;
  deployments: QrDeploymentRecord[];
  codeFormat?: { pattern: string };
};

const file = deploymentsFile as DeploymentsFile;

const CODE_PATTERN = /^[A-Z0-9]{5,8}$/;

function parseDay(isoDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return Number.NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function todayUtcDay(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

function isReviewCurrent(record: QrDeploymentRecord): boolean {
  const due = parseDay(record.reviewDue);
  if (Number.isNaN(due)) return false;
  return due >= todayUtcDay();
}

function normalizeCode(raw: string | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  if (!CODE_PATTERN.test(code)) return null;
  return code;
}

/**
 * Static lookup only. No network, GPS, or analytics.
 * Unknown / disabled / expired-active → inactive (neutral UI; do not distinguish).
 */
export function resolveQrDeployment(
  rawCode: string | undefined,
): QrResolveResult {
  const code = normalizeCode(rawCode);
  if (!code) {
    return { ok: false, reason: "unknown" };
  }

  const deployment = file.deployments.find((d) => d.code === code);
  if (!deployment) {
    return { ok: false, reason: "unknown" };
  }

  if (deployment.status !== "active" || !isReviewCurrent(deployment)) {
    return { ok: false, reason: "inactive" };
  }

  const session: QrSessionContext = {
    resourceSetId: deployment.resourceSetId,
    suggestedMode: deployment.suggestedMode ?? null,
  };

  return { ok: true, deployment, session };
}

export function getQrDeploymentsFile(): DeploymentsFile {
  return file;
}
